import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, AutoComplete, DatePicker, message, Space, Card, Typography, Row, Col, Upload, Tag, Popconfirm, Badge, Statistic, Tooltip, Switch, Alert } from 'antd';
import { PlusOutlined, SearchOutlined, UploadOutlined, DownloadOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, SoundOutlined, CheckCircleOutlined, ClockCircleOutlined, LinkOutlined, GlobalOutlined, CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');

const LYRICIST_PRESETS = ['Shivlal Bainsla', 'Ajeet Bainsla', 'Dayaram Fouji', 'DG Mawai', 'Shersingh Mawai'];
const LANGUAGE_PRESETS = ['Hindi', 'Rajasthani', 'Haryanavi'];
const GENRE_PRESETS = ['World Music', 'Folk'];
const STUDIO_PRESETS = ['Mahakal Studio Kanota', 'Shree Dev Recording Studio Kota', 'BSB Recording Studio', 'Dev Recording Studio Bhilwara'];

export default function SongMetadata({ user }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ total: 0, distributed: 0, pending: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [distFilter, setDistFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [studioFilter, setStudioFilter] = useState('');
  const [distributors, setDistributors] = useState([]);
  const [studios, setStudios] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [imConfigured, setImConfigured] = useState(true);
  const [imBusy, setImBusy] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, search };
      if (dateRange?.[0] && dateRange?.[1]) {
        params.from = dateRange[0].format('YYYY-MM-DD');
        params.to = dateRange[1].format('YYYY-MM-DD');
      }
      if (distFilter) params.distributor = distFilter;
      if (statusFilter) params.distribution_status = statusFilter;
      if (studioFilter) params.studio = studioFilter;
      const res = await api.get('/song-metadata', { params });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
      if (res.data.counts) setCounts(res.data.counts);
      setDistributors(res.data.distributors || []);
      setStudios(res.data.studios || []);
    } catch (e) { message.error('Failed to load song metadata'); }
    setLoading(false);
  }, [page, search, dateRange, distFilter, statusFilter, studioFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get('/intubemusic/status')
      .then(res => setImConfigured(!!res.data.configured))
      .catch(() => {});
  }, []);

  // "Publish on Intube Music" — audio + poster + metadata intubemusic.com par
  // bhejta hai. Wahi gaana dobara bhejne par duplicate nahi banta.
  const publishToIntubeMusic = async (record) => {
    setImBusy(record.id);
    try {
      const res = await api.post(`/intubemusic/publish/${record.id}`);
      message.success(`Intube Music par publish ho gaya: ${res.data.url || record.title}`);
      fetchData();
    } catch (e) {
      message.error(e.response?.data?.error || 'Intube Music par publish nahi hua');
      fetchData();
    }
    setImBusy(null);
  };

  const tagsToStr = (v) => Array.isArray(v) ? v.join(', ') : (v || '');

  const handleAdd = async (vals) => {
    try {
      const payload = { ...vals };
      payload.lyricist = tagsToStr(vals.lyricist);
      payload.language = tagsToStr(vals.language);
      payload.genre = tagsToStr(vals.genre);
      payload.studio = Array.isArray(vals.studio) ? (vals.studio[0] || '') : (vals.studio || '');
      payload.publish_intubemusic = vals.publish_intubemusic ? 'Yes' : 'No';
      if (vals.distribution_date) payload.distribution_date = vals.distribution_date.format('YYYY-MM-DD');
      if (vals.iprs_date) payload.iprs_date = vals.iprs_date.format('YYYY-MM-DD');
      if (vals.release_date) payload.release_date = vals.release_date.format('YYYY-MM-DD');
      const res = await api.post('/song-metadata', payload);
      message.success(res.data.intubemusic_queued
        ? 'Song metadata added! Intube Music par publish hone ke liye laga diya gaya.'
        : 'Song metadata added!');
      setAddModal(false);
      addForm.resetFields();
      fetchData();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to add'); }
  };

  const handleEdit = async (vals) => {
    try {
      const payload = { ...vals };
      payload.lyricist = tagsToStr(vals.lyricist);
      payload.language = tagsToStr(vals.language);
      payload.genre = tagsToStr(vals.genre);
      payload.studio = Array.isArray(vals.studio) ? (vals.studio[0] || '') : (vals.studio || '');
      payload.publish_intubemusic = vals.publish_intubemusic ? 'Yes' : 'No';
      if (vals.distribution_date) payload.distribution_date = vals.distribution_date.format('YYYY-MM-DD');
      else payload.distribution_date = null;
      if (vals.iprs_date) payload.iprs_date = vals.iprs_date.format('YYYY-MM-DD');
      else payload.iprs_date = null;
      if (vals.release_date) payload.release_date = vals.release_date.format('YYYY-MM-DD');
      else payload.release_date = null;
      const res = await api.put(`/song-metadata/${editModal.id}`, payload);
      message.success(res.data.intubemusic_queued
        ? 'Song metadata updated! Intube Music par publish hone ke liye laga diya gaya.'
        : 'Song metadata updated!');
      setEditModal(null);
      editForm.resetFields();
      fetchData();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/song-metadata/${id}`);
      message.success('Deleted');
      fetchData();
    } catch (e) { message.error('Failed to delete'); }
  };

  const handleImport = async () => {
    if (!importFile) { message.warning('Select a CSV or Excel file'); return; }
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await api.post('/song-metadata/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success(res.data.message || 'Import done');
      setImportModal(false);
      setImportFile(null);
      fetchData();
    } catch (e) { message.error(e.response?.data?.error || 'Import failed'); }
    setImporting(false);
  };

  const handleExport = () => {
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/song-metadata/export?search=${encodeURIComponent(search)}&token=${token}`, '_blank');
  };

  const strToTags = (v) => v ? v.split(',').map(s => s.trim()).filter(Boolean) : [];

  const openEdit = (record) => {
    setEditModal(record);
    editForm.setFieldsValue({
      ...record,
      lyricist: strToTags(record.lyricist),
      language: strToTags(record.language),
      genre: strToTags(record.genre),
      studio: record.studio ? [record.studio] : [],
      publish_intubemusic: record.publish_intubemusic === 'Yes',
      distribution_date: record.distribution_date ? dayjs(record.distribution_date) : null,
      iprs_date: record.iprs_date ? dayjs(record.iprs_date) : null,
      release_date: record.release_date ? dayjs(record.release_date) : null,
    });
  };

  const columns = [
    { title: '#', render: (_, __, i) => (page - 1) * 50 + i + 1, width: 45, fixed: 'left' },
    { title: 'Title', dataIndex: 'title', key: 'title', width: 220, fixed: 'left', ellipsis: true,
      render: (t) => <Text strong style={{ fontSize: 13 }}>{t}</Text> },
    { title: 'ISRC', dataIndex: 'isrc', key: 'isrc', width: 140, render: v => v || '-' },
    { title: 'UPC', dataIndex: 'upc', key: 'upc', width: 140, render: v => v || '-' },
    { title: 'Catalog / Code', dataIndex: 'catalog_number', key: 'catalog_number', width: 140, render: v => v || '-' },
    { title: 'Label', dataIndex: 'label', key: 'label', width: 160, render: v => v || '-' },
    { title: 'Singer', dataIndex: 'singer', key: 'singer', width: 160, render: v => v || '-' },
    { title: 'Artist', dataIndex: 'artist', key: 'artist', width: 140, render: v => v || '-' },
    { title: 'Lyricist', dataIndex: 'lyricist', key: 'lyricist', width: 130, render: v => v || '-' },
    { title: 'Composer', dataIndex: 'composer', key: 'composer', width: 130, render: v => v || '-' },
    { title: 'Language', dataIndex: 'language', key: 'language', width: 100, render: v => v || '-' },
    { title: 'Genre', dataIndex: 'genre', key: 'genre', width: 100, render: v => v || '-' },
    { title: 'Studio', dataIndex: 'studio', key: 'studio', width: 180, render: v => v || '-' },
    { title: 'Distribution', dataIndex: 'distribution', key: 'dist', width: 100,
      render: v => v === 'Yes' ? <Tag color="green">Yes</Tag> : <Tag>No</Tag> },
    { title: 'Status', dataIndex: 'distribution_status', key: 'dstatus', width: 110,
      render: v => v === 'distributed' ? <Tag color="success">Distributed</Tag> : <Tag color="default">Pending</Tag> },
    { title: 'Distribution Company', dataIndex: 'distributor', key: 'distributor', width: 160, render: v => v || '-' },
    { title: 'Distribution Date', dataIndex: 'distribution_date', key: 'ddate', width: 130, render: v => v || '-' },
    { title: 'IPRS', dataIndex: 'iprs', key: 'iprs', width: 70,
      render: v => v === 'Yes' ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag> },
    { title: 'IPRS Status', dataIndex: 'iprs_status', key: 'istatus', width: 110, render: v => v || '-' },
    { title: 'IPRS Date', dataIndex: 'iprs_date', key: 'idate', width: 120, render: v => v || '-' },
    { title: 'Release Date', dataIndex: 'release_date', key: 'rdate', width: 120, render: v => v || '-' },
    { title: 'Agreement', dataIndex: 'agreement', key: 'agreement', width: 100,
      render: v => v === 'Yes' ? <Tag color="purple">Yes</Tag> : <Tag>No</Tag> },
    { title: 'iTunes/Apple', dataIndex: 'itunes_link', key: 'itunes', width: 120,
      render: v => v ? <a href={v} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-' },
    { title: 'Spotify', dataIndex: 'spotify_link', key: 'spotify', width: 120,
      render: v => v ? <a href={v} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-' },
    { title: 'Intube Music', key: 'intubemusic', width: 150, render: (_, r) => {
      if (r.intubemusic_status === 'published') {
        return r.intubemusic_url
          ? <a href={r.intubemusic_url} target="_blank" rel="noreferrer"><Tag color="green">Live <LinkOutlined /></Tag></a>
          : <Tag color="green">Live</Tag>;
      }
      if (r.intubemusic_status === 'queued') return <Tag color="processing">Queued</Tag>;
      if (r.intubemusic_status === 'failed') {
        return <Tooltip title={r.intubemusic_error || 'Publish failed'}><Tag color="error">Failed</Tag></Tooltip>;
      }
      return r.publish_intubemusic === 'Yes' ? <Tag color="processing">Queued</Tag> : <Tag>No</Tag>;
    }},
    { title: 'Actions', key: 'actions', width: 190, fixed: 'right', render: (_, r) => (
      <Space size="small">
        <Tooltip title={r.intubemusic_status === 'published' ? 'Intube Music par dobara bhejo (update)' : 'Intube Music par abhi publish karo'}>
          <Button
            size="small"
            icon={r.intubemusic_status === 'failed' ? <ReloadOutlined /> : <CloudUploadOutlined />}
            loading={imBusy === r.id}
            onClick={() => publishToIntubeMusic(r)}
          />
        </Tooltip>
        <Tooltip title="Is gaane ke saare store links (Spotify, Apple, JioSaavn...)">
          <Button
            size="small"
            icon={<GlobalOutlined />}
            onClick={() => navigate(`/store-links?title=${encodeURIComponent(r.title || '')}&singer=${encodeURIComponent(r.singer || '')}&isrc=${encodeURIComponent(r.isrc || '')}&upc=${encodeURIComponent(r.upc || '')}&metadata_id=${r.id || ''}`)}
          />
        </Tooltip>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )},
  ];

  const formFields = (
    <>
      <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title required' }]}>
        <Input placeholder="Song title" />
      </Form.Item>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="isrc" label="ISRC"><Input placeholder="ISRC code" /></Form.Item></Col>
        <Col span={12}><Form.Item name="upc" label="UPC"><Input placeholder="UPC code" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="catalog_number" label="Catalog Number / Code"><Input placeholder="Catalog / code number" /></Form.Item></Col>
        <Col span={12}><Form.Item name="label" label="Label" initialValue="Bainsla Music Private Limited"><Input placeholder="Label (Company Name)" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="singer" label="Singer"><Input placeholder="Singer name" /></Form.Item></Col>
        <Col span={12}><Form.Item name="artist" label="Artist"><Input placeholder="Artist name" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="lyricist" label="Lyricist"><Select mode="tags" placeholder="Select or type lyricist" tokenSeparators={[',']}>{LYRICIST_PRESETS.map(n => <Option key={n} value={n}>{n}</Option>)}</Select></Form.Item></Col>
        <Col span={12}><Form.Item name="composer" label="Composer"><Input placeholder="Composer" /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="language" label="Language"><Select mode="tags" placeholder="Select or type language" tokenSeparators={[',']}>{LANGUAGE_PRESETS.map(n => <Option key={n} value={n}>{n}</Option>)}</Select></Form.Item></Col>
        <Col span={12}><Form.Item name="genre" label="Genre"><Select mode="tags" placeholder="Select or type genre" tokenSeparators={[',']}>{GENRE_PRESETS.map(n => <Option key={n} value={n}>{n}</Option>)}</Select></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="studio" label="Studio"><Select mode="tags" placeholder="Select or type studio" tokenSeparators={[',']} maxCount={1}>{[...new Set([...STUDIO_PRESETS, ...studios])].map(n => <Option key={n} value={n}>{n}</Option>)}</Select></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="distribution" label="Distribution" initialValue="No">
            <Select><Option value="Yes">Yes</Option><Option value="No">No</Option></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="distribution_status" label="Distribution Status" initialValue="pending">
            <Select><Option value="pending">Pending</Option><Option value="distributed">Distributed</Option></Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="distributor" label="Distribution Company"><AutoComplete options={distributors.map(d => ({ value: d }))} filterOption={(i, o) => String(o?.value || '').toLowerCase().includes(String(i || '').toLowerCase())} allowClear placeholder="Distributor chuno ya likho (PDL, Ditto Music…)" /></Form.Item></Col>
        <Col span={12}><Form.Item name="distribution_date" label="Distribution Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}><Form.Item name="release_date" label="Release Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
        <Col span={8}>
          <Form.Item name="agreement" label="Agreement" initialValue="No">
            <Select><Option value="Yes">Yes</Option><Option value="No">No</Option></Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={8}>
          <Form.Item name="iprs" label="IPRS" initialValue="No">
            <Select><Option value="Yes">Yes</Option><Option value="No">No</Option></Select>
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item name="iprs_status" label="IPRS Status" initialValue="pending">
            <Select><Option value="pending">Pending</Option><Option value="approved">Approved</Option><Option value="rejected">Rejected</Option></Select>
          </Form.Item>
        </Col>
        <Col span={8}><Form.Item name="iprs_date" label="IPRS Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}><Form.Item name="itunes_link" label="iTunes/Apple Link"><Input placeholder="https://music.apple.com/..." /></Form.Item></Col>
        <Col span={12}><Form.Item name="spotify_link" label="Spotify Link"><Input placeholder="https://open.spotify.com/..." /></Form.Item></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12, background: '#f6ffed', borderRadius: 8 }}
        title={<span><CloudUploadOutlined /> Intube Music par publish</span>}>
        <Form.Item name="publish_intubemusic" valuePropName="checked" style={{ marginBottom: 4 }}
          label="Is gaane ko intubemusic.com par daalna hai?">
          <Switch checkedChildren="Haan" unCheckedChildren="Nahi" />
        </Form.Item>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tick karte hi gaana (audio + poster + metadata) baki platform ki tarah intubemusic.com par bhi publish ho jata hai.
          Audio Song Library me hona chahiye. Dobara bhejne se duplicate nahi banta.
        </Text>
        {!imConfigured && (
          <Alert style={{ marginTop: 8 }} type="warning" showIcon
            message="Intube Music API key server par set nahi hai — publish nahi hoga." />
        )}
      </Card>
    </>
  );

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}><SoundOutlined /> Song Metadata</Title></Col>
        <Col>
          <Space wrap>
            <Button icon={<UploadOutlined />} onClick={() => setImportModal(true)}>Import CSV/Excel</Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export Excel</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddModal(true); addForm.resetFields(); }}>Add Song</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8} sm={6}>
          <Card onClick={() => { setStatusFilter(''); setPage(1); }} hoverable
            style={{ borderRadius: 10, background: '#e6f7ff', textAlign: 'center', border: statusFilter === '' ? '2px solid #1890ff' : undefined, cursor: 'pointer' }}>
            <Statistic title="Total Songs" value={counts.total} prefix={<SoundOutlined style={{ color: '#1890ff' }} />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => { setStatusFilter('distributed'); setPage(1); }} hoverable
            style={{ borderRadius: 10, background: '#f6ffed', textAlign: 'center', border: statusFilter === 'distributed' ? '2px solid #52c41a' : undefined, cursor: 'pointer' }}>
            <Statistic title="Distributed" value={counts.distributed} prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={8} sm={6}>
          <Card onClick={() => { setStatusFilter('pending'); setPage(1); }} hoverable
            style={{ borderRadius: 10, background: '#fff7e6', textAlign: 'center', border: statusFilter === 'pending' ? '2px solid #faad14' : undefined, cursor: 'pointer' }}>
            <Statistic title="Pending" value={counts.pending} prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Input placeholder="Search title, singer, ISRC, UPC..." prefix={<SearchOutlined />} value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} allowClear />
          </Col>
          <Col xs={24} sm={4}>
            <Select placeholder="Studio" value={studioFilter || undefined} onChange={v => { setStudioFilter(v || ''); setPage(1); }} allowClear style={{ width: '100%' }}>
              {[...new Set([...STUDIO_PRESETS, ...studios])].map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={4}>
            <Select placeholder="Dist. Company" value={distFilter || undefined} onChange={v => { setDistFilter(v || ''); setPage(1); }} allowClear style={{ width: '100%' }}>
              {distributors.map(d => <Option key={d} value={d}>{d}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={3}>
            <Select placeholder="Status" value={statusFilter || undefined} onChange={v => { setStatusFilter(v || ''); setPage(1); }} allowClear style={{ width: '100%' }}>
              <Option value="distributed">Distributed</Option>
              <Option value="pending">Pending</Option>
            </Select>
          </Col>
          <Col xs={24} sm={5}>
            <RangePicker format="DD/MM/YYYY" placeholder={['From', 'To']} value={dateRange}
              onChange={dates => { setDateRange(dates); setPage(1); }} allowClear style={{ width: '100%' }} />
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small"
          scroll={{ x: 2600 }}
          pagination={{ current: page, total, pageSize: 50, onChange: p => setPage(p), showTotal: (t) => `Total: ${t} songs` }} />
      </Card>

      {/* Add Song Modal */}
      <Modal title="Add Song Metadata" open={addModal} onCancel={() => setAddModal(false)} footer={null} width={700}>
        <Form form={addForm} layout="vertical" onFinish={handleAdd} initialValues={{ company: 'Bainsla Music', distribution: 'No', distribution_status: 'pending', iprs: 'No', iprs_status: 'pending', agreement: 'No' }}>
          {formFields}
          <Button type="primary" htmlType="submit" icon={<PlusOutlined />} block>Add Song</Button>
        </Form>
      </Modal>

      {/* Edit Song Modal */}
      <Modal title="Edit Song Metadata" open={!!editModal} onCancel={() => setEditModal(null)} footer={null} width={700}>
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {formFields}
          <Button type="primary" htmlType="submit" icon={<EditOutlined />} block>Save Changes</Button>
        </Form>
      </Modal>

      {/* Import CSV Modal */}
      <Modal title="Import Song Metadata (CSV / Excel)" open={importModal} onCancel={() => { setImportModal(false); setImportFile(null); }}
        footer={[
          <Button key="cancel" onClick={() => { setImportModal(false); setImportFile(null); }}>Cancel</Button>,
          <Button key="import" type="primary" loading={importing} onClick={handleImport} disabled={!importFile}>Import</Button>,
        ]} width={500}>
        <div style={{ marginBottom: 12 }}>
          <Text>Upload a CSV file with columns: Title, ISRC, UPC, Company, Singer, Artist, Lyricist, Composer, Language, Genre, Distribution, Distribution Status, Distribution Company, etc.</Text>
        </div>
        <Upload
          beforeUpload={file => { setImportFile(file); return false; }}
          onRemove={() => setImportFile(null)}
          fileList={importFile ? [importFile] : []}
          accept=".csv,.xlsx,.xls"
          maxCount={1}
        >
          <Button icon={<FileExcelOutlined />}>Select CSV File</Button>
        </Upload>
        <div style={{ marginTop: 12 }}>
          <Text type="secondary">Duplicate songs (same title + ISRC + UPC) will be skipped automatically.</Text>
        </div>
      </Modal>
    </div>
  );
}
